import { v4 as uuid } from "uuid";

import * as EntityEmployee from "@entities/contractorEmployee";
import { OrganisationLineItem } from "./organisation";

export type Employee = EntityEmployee.ContractorEmployee;

export enum Gender {
  Male = "gender_male",
  Female = "gender_female",
  Other = "gender_other",
}

export const genderToString = (gender: Gender): string => {
  switch (gender) {
    case Gender.Male:
      return "Male";
    case Gender.Female:
      return "Female";
    case Gender.Other:
      return "Other";
  }
};

export enum NewExistingJobType {
  New = "new-existing-job-type_new",
  Existing = "new-existing-job-type_existing",
}

export const existingJobTypeToString = (
  newExistingJobType: NewExistingJobType
): string => {
  switch (newExistingJobType) {
    case NewExistingJobType.New:
      return "New";
    case NewExistingJobType.Existing:
      return "Existing";
  }
};

export enum OccupationType {
  TradeRole = "occupation-type-options_trade-role",
  NonTradePosition = "occupation-type-options_non-trade-position",
  ManagementSupervisorSpecialistLabourRoles = "occupation-type-options_management-supervisor-specialist",
  Other = "occupation-type-options_other",
}

export const occupationTypeOptionToString = (
  occupationTypeOption: OccupationType
): string => {
  switch (occupationTypeOption) {
    case OccupationType.TradeRole:
      return "Trade role";
    case OccupationType.NonTradePosition:
      return "Non-trade position";
    case OccupationType.ManagementSupervisorSpecialistLabourRoles:
      return "Management, supervisor & specialist labour roles";
    case OccupationType.Other:
      return "Other";
  }
};

export enum EmploymentType {
  FullEmployment = "employment-type_full-employment",
  Apprentice = "employment-type_apprentice",
  Trainee = "employment-type_trainee",
  Cadet = "employment-type_cadet",
}

export const employmentTypeToString = (
  employmentType: EmploymentType
): string => {
  switch (employmentType) {
    case EmploymentType.FullEmployment:
      return "Full Employment";
    case EmploymentType.Apprentice:
      return "Apprentice";
    case EmploymentType.Trainee:
      return "Trainee";
    case EmploymentType.Cadet:
      return "Cadet";
  }
};

export enum MPSGEmploymentType {
  Apprentice = "mpsg-employment-type_apprentice",
  Trainee = "mpsg-employment-type_trainee",
  Cadet = "mpsg-employment-type_cadet",
}

export const mpsgEmploymentTypesToString = (
  mpsgEmploymentType: MPSGEmploymentType
): string => {
  switch (mpsgEmploymentType) {
    case MPSGEmploymentType.Apprentice:
      return "Apprentice";
    case MPSGEmploymentType.Trainee:
      return "Trainee";
    case MPSGEmploymentType.Cadet:
      return "Cadet";
  }
};

export enum EmployeeSocialProcurementFactorForDisadvantaged {
  AsylumSeekerRefugee = "employee-social-procurement-factor_asylum-seeker-refugee",
  DisengagedYouth = "employee-social-procurement-factor_disengaged-youth",
  LongTermUnemployed = "employee-social-procurement-factor_long-term-unemployed",
  PersonWithDisability = "employee-social-procurement-factor_person-with-disability",
  Veteran = "employee-social-procurement-factor_verteran",
  CulturallyAndLinguisticallyDiverse = "employee-social-procurement-factor_culterually-and-linguistically-diverse",
}

export enum EmployeeSocialProcurementFactor {
  LocalJobsFirst = "employee-social-procurement-factor_local-jobs-first",
  AborignialOrTorresStraitIslander = "employee-social-procurement-factor_aborignial-or-torres-strait-islander",
  AsylumSeekerRefugee = "employee-social-procurement-factor_asylum-seeker-refugee",
  DisengagedYouth = "employee-social-procurement-factor_disengaged-youth",
  LongTermUnemployed = "employee-social-procurement-factor_long-term-unemployed",
  PersonWithDisability = "employee-social-procurement-factor_person-with-disability",
  Veteran = "employee-social-procurement-factor_verteran",
  CulturallyAndLinguisticallyDiverse = "employee-social-procurement-factor_culterually-and-linguistically-diverse",
  PersonsAffectedByFamilyViolence = "employee-social-procurement-factor_person-affected-by-family-violence",
}

export const employeeSocialProcurementFactorToString = (
  employeeSocialProcurementFactor:
    | EmployeeSocialProcurementFactor
    | EmployeeSocialProcurementFactorForDisadvantaged
): string => {
  switch (employeeSocialProcurementFactor) {
    case EmployeeSocialProcurementFactor.LocalJobsFirst:
      return "Local Jobs First";
    case EmployeeSocialProcurementFactor.AborignialOrTorresStraitIslander:
      return "Aborignial or Torres-Strait Islander";
    case EmployeeSocialProcurementFactorForDisadvantaged.AsylumSeekerRefugee:
    case EmployeeSocialProcurementFactor.AsylumSeekerRefugee:
      return "Asylum Seeker/Refugee";
    case EmployeeSocialProcurementFactorForDisadvantaged.DisengagedYouth:
    case EmployeeSocialProcurementFactor.DisengagedYouth:
      return "Disengaged youth - aged between 15-24";

    case EmployeeSocialProcurementFactorForDisadvantaged.LongTermUnemployed:
    case EmployeeSocialProcurementFactor.LongTermUnemployed:
      return "Long-term unemployed";
    case EmployeeSocialProcurementFactorForDisadvantaged.PersonWithDisability:
    case EmployeeSocialProcurementFactor.PersonWithDisability:
      return "Person with disability (inclusive of mental illness)";
    case EmployeeSocialProcurementFactorForDisadvantaged.Veteran:
    case EmployeeSocialProcurementFactor.Veteran:
      return "Veteran (ADF or Allied Forces)";
    case EmployeeSocialProcurementFactorForDisadvantaged.CulturallyAndLinguisticallyDiverse:
    case EmployeeSocialProcurementFactor.CulturallyAndLinguisticallyDiverse:
      return "Culturally & Linguistically Diverse";
    case EmployeeSocialProcurementFactor.PersonsAffectedByFamilyViolence:
      return "Persons Affected by Family Violence";
  }
};

export interface EmployeeLineItem {
  id: string;
  contractorId: string;
  employeeName: string;
  gender: Gender;
  postCode: string;
  country: string;
  primaryEmployer: string;
  occupation: string;
  occupationType: OccupationType;
  newExistingJob: NewExistingJobType;
  employmentType: EmploymentType;
  socialFactors: EmployeeSocialProcurementFactor[];
  created: Date;
}

export const createDefaultEmployee = (
  contractor?: OrganisationLineItem
): EmployeeLineItem => {
  return {
    id: uuid(),
    contractorId: contractor?.id || "",
    employeeName: "",
    gender: Gender.Male,
    country: "AU",
    postCode: "",
    primaryEmployer: "",
    occupation: "",
    occupationType: OccupationType.TradeRole,
    newExistingJob: NewExistingJobType.Existing,
    employmentType: EmploymentType.FullEmployment,
    socialFactors: [],
    created: new Date(),
  };
};
